/**
 * src/three/RageBulletDom.js
 * กระสุนข้อความ — stamp คอมมิค (ขนาดคงที่ ไม่มีสไปรต์ 3D)
 */
import { RAGE_PHRASES, T3 } from './config.js';

const MAX_LIVE = 12;
const SIZE_PX = 22;

function randomPhrase() {
  return RAGE_PHRASES[Math.floor(Math.random() * RAGE_PHRASES.length)];
}

function easeOutQuad(t) {
  return 1 - (1 - t) * (1 - t);
}

export default class RageBulletDom {
  constructor() {
    this.layer = document.createElement('div');
    this.layer.className = 'rage-layer';
    this.layer.setAttribute('data-rage-layer', '1');
    document.body.appendChild(this.layer);
    /** @type {HTMLElement[]} */
    this.active = [];
  }

  /**
   * @param {{ x: number, y: number }|null} fromScreen
   * @param {{ x: number, y: number }|null} toScreen
   * @param {() => void} onArrive
   * @param {string} [phrase]
   * @param {{ berserk?: boolean, big?: boolean }} [opts]
   */
  spawn(fromScreen, toScreen, onArrive, phrase, opts = {}) {
    if (!this.layer) {
      this.layer = document.createElement('div');
      this.layer.className = 'rage-layer';
      this.layer.setAttribute('data-rage-layer', '1');
      document.body.appendChild(this.layer);
    }
    this.active = this.active.filter((el) => el.isConnected);
    if (this.active.length >= MAX_LIVE) {
      return;
    }

    const w = window.innerWidth || 800;
    const h = window.innerHeight || 600;
    const from = fromScreen && Number.isFinite(fromScreen.x)
      ? { x: fromScreen.x, y: fromScreen.y }
      : { x: w * 0.7, y: h * 0.78 };
    const to = toScreen && Number.isFinite(toScreen.x)
      ? { x: toScreen.x, y: toScreen.y }
      : { x: w * 0.5, y: h * 0.48 };

    const text = phrase || randomPhrase();
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const size = opts.berserk ? 32 : opts.big ? 26 : SIZE_PX;

    const el = document.createElement('div');
    el.className = 'rage-shot' + (opts.berserk ? ' rage-shot--berserk' : '');
    el.setAttribute('data-rage-shot', '1');
    el.textContent = text;
    el.style.fontSize = `${size}px`;
    el.style.left = `${from.x}px`;
    el.style.top = `${from.y}px`;
    this.layer.appendChild(el);

    const flightMs = (T3.WORD_FLIGHT ?? 0.4) * 1000;
    const t0 = performance.now();
    let done = false;

    const tick = (now) => {
      if (!el.isConnected) return;
      const u = Math.min(1, (now - t0) / flightMs);
      const e = easeOutQuad(u);
      const x = from.x + dx * e;
      const y = from.y + dy * e;
      const scale = 1.05 - e * 0.18;

      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.transform = `translate(-50%, -50%) scale(${scale})`;
      el.style.opacity = String(u < 0.05 ? u / 0.05 : 1 - Math.max(0, (u - 0.92) / 0.08) * 0.25);

      if (u < 1) {
        requestAnimationFrame(tick);
        return;
      }
      if (!done) {
        done = true;
        el.classList.add('rage-shot--hit');
        try { onArrive?.(); } catch (_) { /* ignore */ }
        window.setTimeout(() => {
          el.remove();
          const i = this.active.indexOf(el);
          if (i >= 0) this.active.splice(i, 1);
        }, 60);
      }
    };

    requestAnimationFrame(tick);
    this.active.push(el);
  }

  update() {}

  clear() {
    this.clearShots();
    this.layer?.remove();
    this.layer = null;
  }

  clearShots() {
    for (const el of this.active) el.remove?.();
    this.active.length = 0;
    if (this.layer) this.layer.replaceChildren();
    document.querySelectorAll('[data-rage-shot]').forEach((n) => n.remove());
  }
}
